// THE DOCUMENT MODAL for a Klargo paper (the Lovable design's DocumentPreviewDialog, `paper` kind) —
// the same modal the signed contract opens in (ContractPreviewModal): it opens ABOVE the page, the
// page stays mounted behind a restrained ink scrim, and closing puts the user back exactly where
// they were. One sticky header — the document's name, its download, its close control — over a
// single scroll surface. An A4 page renders at its own 100% (794 CSS px) wherever the column allows.
//
// "Download PDF" downloads what is on screen as a real PDF (the contract's own helper), where the
// reference only simulates it.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { downloadElementAsPdf } from "../../lib/contractPdf";
import PaperDocumentView from "./PaperDocumentView";

/** A4 at 96 dpi — the natural size of a page, and the widest a paper renders. */
const NATURAL_PAGE_WIDTH = 794;

/** Workspace beside the page inside the modal. Never enough to shrink the page on a phone. */
const gutterFor = (width) => (width < 480 ? 10 : width < 900 ? 16 : 28);
const verticalPaddingFor = (width) => (width < 480 ? 12 : width < 900 ? 16 : 24);

export default function PaperPreviewModal({ open, onClose, title, document: paper, fileName, labels }) {
  const modalRef = useRef(null);
  const scrollRef = useRef(null);
  const closeRef = useRef(null);
  const openerRef = useRef(null);
  const printRef = useRef(null);
  const [surfaceWidth, setSurfaceWidth] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1024));
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = window.document.activeElement;
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 60);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKeyDown);
      window.document.body.style.overflow = previousOverflow;
      const opener = openerRef.current;
      if (opener instanceof HTMLElement && opener.isConnected) window.setTimeout(() => opener.focus(), 0);
    };
  }, [open, onClose]);

  // The page scale follows the modal's own scroll surface: the surface is what the sheet has to fit
  // in, whatever the modal's margins currently are.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const surface = scrollRef.current;
    if (!surface) return undefined;
    const measure = () => setSurfaceWidth(surface.clientWidth || window.innerWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(measure);
    });
    observer.observe(surface);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [open]);

  // Tab stays inside the modal for as long as it is open.
  const trapTab = useCallback((event) => {
    if (event.key !== "Tab") return;
    const root = modalRef.current;
    if (!root) return;
    const focusable = Array.from(root.querySelectorAll('button, a[href], [tabindex]:not([tabindex="-1"])'))
      .filter((el) => el.offsetParent !== null);
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && window.document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && window.document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  const handleDownload = async () => {
    if (!printRef.current || downloading) return;
    setDownloading(true);
    try {
      await downloadElementAsPdf(printRef.current, fileName);
    } catch (err) {
      console.error("PDF download failed", err);
    } finally {
      setDownloading(false);
    }
  };

  if (!open || !paper) return null;

  const gutter = gutterFor(surfaceWidth);
  const verticalPadding = verticalPaddingFor(surfaceWidth);
  const columnWidth = Math.max(200, surfaceWidth - gutter * 2);
  const paperWidth = Math.min(columnWidth, NATURAL_PAGE_WIDTH);

  return (
    <div className="ds-scope ds-animate-veil ds-viewer-scrim fixed inset-0 z-[80] flex" data-testid="document-viewer">
      <div role="dialog" aria-modal="true" aria-label={title} ref={modalRef} onKeyDown={trapTab}
        data-testid="document-preview" className="ds-viewer-modal ds-animate-layer">
        <div data-testid="viewer-toolbar" className="ds-viewer-modal-header flex shrink-0 items-center gap-3 px-3.5 min-[600px]:px-5">
          <span data-testid="viewer-title" className="min-w-0 flex-1 truncate text-[15px] font-semibold leading-none text-ds-ink">
            {title}
          </span>
          <button type="button" data-testid="viewer-download" onClick={handleDownload} disabled={downloading}
            className="ds-viewer-download ds-hit">
            <svg width="15" height="15" viewBox="0 0 14 14" aria-hidden="true"
              style={{ fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" }}>
              <path d="M7 1.6v7.2" />
              <path d="M4 6.2 7 9.2l3-3" />
              <path d="M2 11.4h10" />
            </svg>
            <span>{labels.download}</span>
          </button>
          <button type="button" ref={closeRef} data-testid="viewer-close" onClick={onClose} aria-label={labels.close}
            className="ds-viewer-close ds-hit">
            <svg width="15" height="15" viewBox="0 0 14 14" aria-hidden="true"
              style={{ fill: "none", stroke: "currentColor", strokeWidth: 1.7, strokeLinecap: "round" }}>
              <line x1="2" y1="2" x2="12" y2="12" />
              <line x1="12" y1="2" x2="2" y2="12" />
            </svg>
          </button>
        </div>

        {/* The one document scroll surface. Pages scroll under the header. */}
        <div ref={scrollRef} data-testid="viewer-scroll"
          className="ds-viewer-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden"
          style={{ padding: `${verticalPadding}px ${gutter}px ${verticalPadding + 12}px` }}>
          <div className="mx-auto" style={{ width: paperWidth }}>
            <div ref={printRef} className="bg-transparent">
              <PaperDocumentView document={paper} pageOf={labels.pageOf} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
